import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const VideoNotes = ({ 
  notes = [], 
  currentTime = 0, 
  onAddNote, 
  onDeleteNote, 
  onEditNote,
  onSeekToNote 
}) => {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAddNote = () => {
    if (!newNoteText?.trim()) return;

    const newNote = {
      id: Date.now(),
      text: newNoteText?.trim(),
      timestamp: currentTime,
      createdAt: new Date()?.toISOString()
    };

    onAddNote?.(newNote);
    setNewNoteText('');
    setIsAddingNote(false);
  };

  const handleEditNote = (noteId) => {
    if (!editNoteText?.trim()) return;

    onEditNote?.(noteId, editNoteText?.trim());
    setEditingNoteId(null);
    setEditNoteText('');
  };

  const startEditing = (note) => {
    setEditingNoteId(note?.id);
    setEditNoteText(note?.text);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditNoteText('');
  };

  const sortedNotes = [...notes]?.sort((a, b) => a?.timestamp - b?.timestamp);

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="FileText" size={20} className="text-foreground" />
          <h3 className="font-heading font-medium text-foreground">
            Minhas Anotações ({notes?.length})
          </h3>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={() => setIsAddingNote(true)}
          iconName="Plus"
          iconPosition="left"
          iconSize={16}
        >
          Nova Nota
        </Button>
      </div>
      {/* Add Note Form */}
      {isAddingNote && (
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="space-y-3">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground font-caption">
              <Icon name="Clock" size={14} />
              <span>Tempo atual: {formatTime(currentTime)}</span>
            </div>
            
            <Input
              type="text"
              placeholder="Digite sua anotação..."
              value={newNoteText}
              onChange={(e) => setNewNoteText(e?.target?.value)}
              onKeyPress={(e) => {
                if (e?.key === 'Enter') {
                  handleAddNote();
                }
              }}
              className="w-full"
            />
            
            <div className="flex items-center space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleAddNote}
                disabled={!newNoteText?.trim()}
              >
                Salvar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsAddingNote(false);
                  setNewNoteText('');
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Notes List */}
      <div className="max-h-96 overflow-y-auto">
        {sortedNotes?.length === 0 ? (
          <div className="text-center py-8 px-4">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="font-heading font-medium text-foreground mb-2">
              Nenhuma anotação ainda
            </h4>
            <p className="text-muted-foreground font-caption mb-4">
              Adicione anotações durante o vídeo para lembrar de pontos importantes.
            </p>
            <Button
              variant="outline"
              onClick={() => setIsAddingNote(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Criar primeira anotação
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sortedNotes?.map((note) => (
              <div key={note?.id} className="p-4 hover:bg-accent/50 transition-micro">
                {editingNoteId === note?.id ? (
                  /* Edit Mode */
                  (<div className="space-y-3">
                    <Input
                      type="text"
                      value={editNoteText}
                      onChange={(e) => setEditNoteText(e?.target?.value)}
                      onKeyPress={(e) => {
                        if (e?.key === 'Enter') {
                          handleEditNote(note?.id);
                        }
                      }}
                      className="w-full"
                    />
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleEditNote(note?.id)}
                        disabled={!editNoteText?.trim()}
                      >
                        Salvar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={cancelEditing}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>)
                ) : (
                  /* View Mode */
                  (<div className="space-y-3">
                    {/* Note Header */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => onSeekToNote?.(note?.timestamp)}
                        className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-micro"
                      >
                        <Icon name="Play" size={14} />
                        <span className="text-sm font-medium font-caption">
                          {formatTime(note?.timestamp)}
                        </span>
                      </button>
                      
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditing(note)}
                          iconName="Edit"
                          iconSize={14}
                          className="text-muted-foreground hover:text-foreground p-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteNote?.(note?.id)}
                          iconName="Trash2"
                          iconSize={14}
                          className="text-muted-foreground hover:text-destructive p-1"
                        />
                      </div>
                    </div>
                    {/* Note Content */}
                    <p className="text-foreground leading-relaxed">
                      {note?.text}
                    </p>
                    {/* Note Meta */}
                    <p className="text-xs text-muted-foreground font-caption">
                      Criado em {formatDate(note?.createdAt)}
                    </p>
                  </div>)
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Footer */}
      {notes?.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-sm text-muted-foreground font-caption">
            <span>{notes?.length} anotação{notes?.length !== 1 ? 'ões' : ''}</span>
            <Button
              variant="ghost"
              size="sm"
              iconName="Download"
              iconSize={14}
              className="text-muted-foreground hover:text-foreground"
            >
              Exportar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoNotes;